// ABOUTME: React Error Boundary component to catch and handle React rendering errors
// ABOUTME: Provides user-friendly error display and error reporting functionality

import { ErrorOutline, Refresh } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import React, { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo: errorInfo.componentStack || undefined,
    });

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
    }

    // In production, you could send this to an error reporting service
    // reportError(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          p={3}
        >
          <ErrorOutline sx={{ fontSize: 64, color: "error.main", mb: 2 }} />

          <Typography variant="h5" gutterBottom>
            Something went wrong
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            mb={3}
          >
            We're sorry, but something unexpected happened. Please try
            refreshing the page or contact support if the problem persists.
          </Typography>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <Alert severity="error" sx={{ mb: 2, maxWidth: "100%" }}>
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}
              >
                {this.state.error.message}
                {this.state.errorInfo && `\n\n${this.state.errorInfo}`}
              </Typography>
            </Alert>
          )}

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={this.handleRetry}
            >
              Try Again
            </Button>
            <Button variant="outlined" onClick={this.handleReload}>
              Reload Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with ErrorBoundary
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
) => {
  const WrappedComponent = React.forwardRef<unknown, P>((props, ref) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...(props as P)} ref={ref} />
    </ErrorBoundary>
  ));

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};
